import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AllLabels, FacilityFormTypes, InstallationFlowType } from '../../../constants/app.constants';

import { FormTypesData, KeyValueData } from '../../../domains/common.domain';
import { CommonService } from '../../../services/common.service';
import { AllDropdownKeys, UrlSegments } from '../../jurisdiction.constants';
import { ComponentFormData, LookupOptionsData } from '../../jurisdiction.domain';
import { FacilitiesFormService } from '../facilities-form.service';
import { CommunicationService } from '../../../services/communication.service';
import { ErrorService } from '../../../services/error/error.service';
import { EnvironmentService } from '../../../services/environment.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { JurisdictionService } from '../../jurisdiction.service';
import { FacilitiesHeatingHiddenFields, FacilitiesHiddenDropdownData } from '../../../interface/config.interface';
import { BaseDataObject, GasDeviceObject } from '../facilities-form.domain';
import { DestroyableComponent } from 'src/app/common/destroyable.component';

@Component({
  selector: 'app-base-data',
  styleUrls: ['./base-data.component.scss'],
  templateUrl: 'base-data.component.html',
})
export class BaseDataComponent extends DestroyableComponent implements OnInit {
  @Input() currentFormType: FormTypesData;
  @Input() baseData: BaseDataObject = null;
  @Input() currentIndex: number = 0;
  @Input() isReadOnly: boolean = false;
  @Input() baseDataLength: number = 0;
  @Output() deleteComponent: EventEmitter<number>;

  baseDataFG: FormGroup;
  facilityTypeKey: string;
  facilityTypes: LookupOptionsData[];
  formSubscription: Subscription;
  gasDeviceCount: number[];
  waterDeviceCount: number[];
  selectedFacilityType: string;
  gasUsageForKey: string;
  gasUsageForOptions: LookupOptionsData[];
  placeOfMeterKey: string;
  placesOfMeter: LookupOptionsData[];
  specialApplianceCount: number[];
  showDeviceAreaText: boolean;
  showData: boolean = true;
  showGasDeviceSection: boolean;
  showRegistrationInputField: boolean;
  currentInstallationFlow: string;
  showDefaultDeviceSubSection: boolean;
  showSpecialApplienceSection: boolean = this.environmentService.showSpecialApplienceSection;

  specialApplianceData: any[];
  gasDeviceData: any[];
  waterDeviceData: any[];
  heatingDeviceData: any;
  hiddenFields: FacilitiesHeatingHiddenFields;
  hiddenDropdownData: FacilitiesHiddenDropdownData;
  allUnits: KeyValueData;
  measuringConcepts: LookupOptionsData[];
  typeOfMeters: LookupOptionsData[];
  showMeasuringConceptAndMeterType: boolean = false;
  measuringConceptKey: string = AllDropdownKeys.FeedInMeasuringConcept;
  typeOfMetertKey: string = AllDropdownKeys.PowerMeterType;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    public environmentService: EnvironmentService,
    private errorService: ErrorService,
    private facilitiesFormService: FacilitiesFormService,
    private localStorageService: LocalStorageService,
    private cdr: ChangeDetectorRef,
    private jurisdictionService: JurisdictionService
  ) {
    super();

    this.gasDeviceCount = [];
    this.waterDeviceCount = [];
    this.specialApplianceCount = [];
    this.selectedFacilityType = null;
    this.deleteComponent = new EventEmitter<number>();
    this.allUnits = AllLabels.units;
  }

  ngOnInit(): void {
    this.showDeviceAreaText = false;
    this.currentInstallationFlow = this.localStorageService.getItem('installation_flow');
    this.hiddenFields = this.environmentService.facilityHeatingHiddenFields;
    this.hiddenDropdownData = this.environmentService.facilityHiddenDropdownData;
    this.showGasDeviceSection = this.environmentService.showGasDeviceSection;
    this.showRegistrationInputField = this.environmentService.showRegistrationInputField;
    this.showDefaultDeviceSubSection = this.environmentService.showDefaultDeviceSubSection;
    this.showMeasuringConceptAndMeterType = this.showMeterMountingAndChangeInstallationAdditionalData && this.isPowerForm;

    this.prepareDropdownData();
    this.prepareForm();
    this.updateParentOnInitialize();

    this.handleChildForms();
    this.facilitiesFormService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.resetData();
      }
    });
    this.jurisdictionService.setMediaType(this.currentFormType.key).subscribe(isPower => {
      this.showData = isPower;
    });
  }

  get showFacilityAdditionalData(): boolean {
    return (
      !this.isPowerForm ||
      this.selectedFacilityType !== 'generation_facility' ||
      !this.environmentService.hideGenerationFacilityAdditionalData
    );
  }

  get showGasUsageForComercial(): boolean {
    return this.currentFormType.key === FacilityFormTypes.Gas.key && this.environmentService.showGasTechnicalDataForComercial;
  }

  get isPowerForm(): boolean {
    return this.currentFormType.key === FacilityFormTypes.Power.key;
  }

  get isHeatingForm(): boolean {
    return this.currentFormType.key === FacilityFormTypes.Heating.key;
  }

  get isWaterForm(): boolean {
    return this.currentFormType.key === FacilityFormTypes.Water.key;
  }

  get isGasForm(): boolean {
    return this.currentFormType.key === FacilityFormTypes.Gas.key;
  }

  get showWaterSystemComponents(): boolean {
    return this.isWaterForm && this.environmentService.showWaterSystemComponents;
  }

  get hideWaterTapOptionsForHousing(): boolean {
    return (
      this.currentFormType.key === FacilityFormTypes.Water.key &&
      this.selectedFacilityType !== 'commercial' &&
      this.environmentService.hideAddWaterTapOption
    );
  }

  get hideWaterTapOptionsForCommercial(): boolean {
    return (
      this.currentFormType.key === FacilityFormTypes.Water.key &&
      this.selectedFacilityType === 'commercial' &&
      this.environmentService.hideAddWaterTapOption
    );
  }

  get isPlaceOfMeterOptional(): boolean {
    return this.environmentService.isPlaceOfMeterOptional && this.selectedFacilityType === 'other' && this.isWaterForm;
  }

  get showMeterMountingAndChangeInstallationAdditionalData(): boolean {
    return (
      this.environmentService.showMeterMountingAndChangeInstallationAdditionalData &&
      (this.currentInstallationFlow === InstallationFlowType.UpdateInstallation ||
        this.currentInstallationFlow === InstallationFlowType.NewInstallation)
    );
  }

  get showMeterLocationDesignation(): boolean {
    return (
      this.environmentService.showMeterMountingAndChangeInstallationAdditionalData &&
      this.currentInstallationFlow === InstallationFlowType.NewInstallation &&
      this.isPowerForm
    );
  }

  prepareForm(): void {
    const selectedFacility =
      (this.baseData && this.baseData.facilityType && this.facilityTypes.filter(item => item.value === this.baseData.facilityType)[0]) ||
      null;
    this.selectedFacilityType = (selectedFacility && selectedFacility.value) || '';

    const placeOfMeter =
      (this.baseData && this.baseData.placeOfMeter && this.placesOfMeter.filter(item => item.value === this.baseData.placeOfMeter)[0]) ||
      null;
    const placeOfMeterValue = (placeOfMeter && placeOfMeter.value) || '';

    const gasUsageFor = this.baseData?.gasUsageFor && this.gasUsageForOptions.find(item => item.value === this.baseData.gasUsageFor);
    const gasUsageForValue = gasUsageFor?.value || '';

    this.baseDataFG = new FormGroup({
      facilityId: new FormControl(this.baseData?.facilityId || ''),
      installationId: new FormControl(this.baseData?.installationId || ''),
      sector: new FormControl(this.baseData?.sector || ''),
      internalConsumptionCheckbox: new FormControl(this.baseData?.internalConsumptionCheckbox || false),
      storageSystemCheckbox: new FormControl(this.baseData?.storageSystemCheckbox || false),
      ...(this.currentFormType.key === 'water' &&
        this.environmentService.showMeasuringPointCheckbox && {
          measuringPointCheckbox: new FormControl(this.baseData?.measuringPointCheckbox || false),
        }),
      placeOfMeter: new FormControl(placeOfMeterValue),
      gasUsageFor: new FormControl(gasUsageForValue),
      sumFlowRate: new FormControl(this.baseData?.sumFlowRate || ''),
      peakFlowRate: new FormControl(this.baseData?.peakFlowRate || '', this.isPeakFlowRateRequired ? [Validators.required] : []),
      otherLocation: new FormControl(this.baseData?.otherLocation || ''),
      gasDevice: new FormArray([]),
      specialAppliance: new FormArray([]),
      waterDevice: new FormArray([]),
      heatingDevice: new FormGroup({}),
      ...(this.currentFormType.key === 'gas' &&
        this.environmentService.showTotalHeatOutput &&
        (this.currentInstallationFlow === InstallationFlowType.UpdateInstallation ||
          this.currentInstallationFlow === InstallationFlowType.NewInstallation) && {
          totalHeatOutput: new FormControl(this.baseData?.totalHeatOutput || ''),
        }),
    });
    const installationFlowTypeElements = [
      InstallationFlowType.NewInstallation,
      InstallationFlowType.UpdateInstallation,
      InstallationFlowType.ConversionToGas,
    ];
    const displayOldFormViewInDetails =
      this.isReadOnly &&
      this.commonService.applicationSubmissionDate &&
      this.environmentService.oldApplicationValidationDate &&
      this.commonService.applicationSubmissionDate < this.environmentService.oldApplicationValidationDate;
    if (
      this.environmentService.showGasAppliance &&
      this.currentFormType.index === 1 &&
      installationFlowTypeElements.includes(this.currentInstallationFlow) &&
      !displayOldFormViewInDetails
    ) {
      this.baseDataFG.addControl(
        'oldPowerGridConnection',
        new FormControl(this.baseData?.oldPowerGridConnection || '', [Validators.required])
      );
      this.baseDataFG.addControl(
        'newPowerGridConnection',
        new FormControl(this.baseData?.newPowerGridConnection || '', [Validators.required])
      );
    }
    if (!this.isHeatingForm) {
      this.baseDataFG.controls.placeOfMeter.setValidators([Validators.required]);
      this.baseDataFG.controls.placeOfMeter.updateValueAndValidity();
    }

    if (!this.hiddenFields.facilityType || !this.isHeatingForm) {
      this.baseDataFG.addControl('facilityType', new FormControl(this.selectedFacilityType, [Validators.required]));
      if (this.selectedFacilityType) {
        this.onFacilityTypeChange();
      }
    }

    if (this.showWaterSystemComponents) {
      this.baseDataFG.addControl('waterPipeMaterial', new FormControl(this.baseData?.waterPipeMaterial || ''));
      this.baseDataFG.addControl('waterInstallationType', new FormControl(this.baseData?.waterInstallationType || ''));
      this.baseDataFG.addControl('waterRefluxFuse', new FormControl(this.baseData?.waterRefluxFuse || ''));
      this.baseDataFG.addControl('waterDevices', new FormControl(this.baseData?.waterDevices || ''));
    }

    if (this.showMeterLocationDesignation) {
      this.baseDataFG.addControl('meterLocationDesignation', new FormControl(this.baseData?.meterLocationDesignation || '', []));
    }

    if (this.showMeterMountingAndChangeInstallationAdditionalData) {
      this.baseDataFG.addControl('reasonForRequest', new FormControl(this.baseData?.reasonForRequest || '', [Validators.required]));

      if (this.isNewInstallation && this.isWaterForm) {
        this.baseDataFG.addControl('vrTotalVolume', new FormControl(this.baseData?.vrTotalVolume || ''));
      }

      if (this.isHeatingForm) {
        this.baseDataFG.addControl('heatedLivingSpace', new FormControl(this.baseData?.heatedLivingSpace || ''));
        this.baseDataFG.addControl('volumeFlowQmax', new FormControl(this.baseData?.volumeFlowQmax || ''));
        this.baseDataFG.addControl('flowTemperature', new FormControl(this.baseData?.flowTemperature || ''));
        this.baseDataFG.addControl('returnTemperature', new FormControl(this.baseData?.returnTemperature || ''));
      }
    }

    if (this.showMeasuringConceptAndMeterType) {
      this.baseDataFG.addControl('measuringConcept', new FormControl(this.baseData?.measuringConcept || '', Validators.required));
      this.baseDataFG.addControl('typeOfMeter', new FormControl(this.baseData?.typeOfMeter || '', Validators.required));
    }

    this.prepareData();
    if (!this.isReadOnly) {
      if (this.showDefaultDeviceSubSection) {
        if (!this.specialApplianceCount.length) {
          this.addSpecialAppliance();
        }
        if (!this.waterDeviceCount.length) {
          this.addWaterDevice();
        }
      }
      if (!this.gasDeviceCount.length) {
        this.addGasDevice();
      }
    }
    this.formSubscription = this.baseDataFG.valueChanges.subscribe(() => {
      this.facilitiesFormService.registerForComponentFormUpdate.next({
        component: 'baseData',
        formData: this.baseDataFG,
        index: this.currentIndex,
      });
    });
    if (
      this.isGasForm &&
      this.environmentService.showTotalHeatOutput &&
      (this.currentInstallationFlow === InstallationFlowType.UpdateInstallation ||
        this.currentInstallationFlow === InstallationFlowType.NewInstallation)
    ) {
      this.preventLeak(this.baseDataFG.controls.gasDevice.valueChanges).subscribe((values: GasDeviceObject[]) => {
        const devices = values.map(device => ({ ...device, ...device.customFields }));

        this.updateTotalHeatOutputData(devices);
        this.cdr.detectChanges();
      });
    }

    if (
      this.environmentService.hasSimultaneouslyRequiredTotalPowerAfterInstallationChange &&
      this.currentInstallationFlow === InstallationFlowType.UpdateInstallation &&
      this.isPowerForm
    ) {
      this.baseDataFG.addControl(
        'simultaneouslyRequiredTotalPowerAfterInstallationChange',
        new FormControl(this.baseData?.simultaneouslyRequiredTotalPowerAfterInstallationChange, Validators.required)
      );
    }
  }

  prepareDropdownData(): void {
    this.facilityTypeKey =
      this.currentFormType.key === 'water' &&
      this.currentInstallationFlow === InstallationFlowType.NewInstallation &&
      this.environmentService.showDropdownsForNewInstallation
        ? AllDropdownKeys.FacilityTypeNew[this.currentFormType.key]
        : AllDropdownKeys.FacilityType[this.currentFormType.key];

    this.placeOfMeterKey =
      this.currentFormType.key === 'water'
        ? this.currentInstallationFlow === InstallationFlowType.NewInstallation && this.environmentService.showDropdownsForNewInstallation
          ? AllDropdownKeys.WaterDeviceLocationNew
          : AllDropdownKeys.WaterDeviceLocation
        : this.currentFormType.key === 'gas'
        ? AllDropdownKeys.GasDeviceLocation
        : this.currentFormType.key === 'heating'
        ? AllDropdownKeys.HeatingDeviceLocation
        : AllDropdownKeys.DeviceLocation;

    this.gasUsageForKey = AllDropdownKeys.GasUsageFor;

    this.facilityTypes = this.commonService.getDropdownData(this.facilityTypeKey);
    this.modifyDropdownCollections();
    this.placesOfMeter = this.commonService.getDropdownData(this.placeOfMeterKey);
    this.gasUsageForOptions = this.commonService.getDropdownData(this.gasUsageForKey);

    if (this.showMeasuringConceptAndMeterType) {
      this.measuringConcepts = this.commonService.getDropdownData(AllDropdownKeys.FeedInMeasuringConcept);
      this.typeOfMeters = this.commonService.getDropdownData(AllDropdownKeys.PowerMeterType);
    }
  }

  prepareData(): void {
    this.specialApplianceData = (this.baseData && this.baseData.specialAppliance) || [];
    this.gasDeviceData = (this.baseData && this.baseData.gasDevice) || [];
    this.waterDeviceData = (this.baseData && this.baseData.waterDevice) || [];
    this.heatingDeviceData = (this.baseData && this.baseData.heatingDevice) || null;
    this.specialApplianceCount = [...Array(this.specialApplianceData.length).keys()];
    this.gasDeviceCount = [...Array(this.gasDeviceData.length).keys()];
    this.waterDeviceCount = [...Array(this.waterDeviceData.length).keys()];
  }

  handleChildForms(): void {
    this.facilitiesFormService.registerForGasDeviceUpdate.subscribe((data: ComponentFormData) => {
      if (data.parentIndex === this.currentIndex) {
        (this.baseDataFG.controls.gasDevice as FormArray).push(data.formData);
      }
    });

    this.facilitiesFormService.registerForSpecialApplianceUpdate.subscribe((data: ComponentFormData) => {
      if (data.parentIndex === this.currentIndex) {
        (this.baseDataFG.controls.specialAppliance as FormArray).push(data.formData);
        this.evaluateDeviceTextVisibility();
      }
    });

    this.facilitiesFormService.registerForWaterDeviceUpdate.subscribe((data: ComponentFormData) => {
      if (data.parentIndex === this.currentIndex) {
        (this.baseDataFG.controls.waterDevice as FormArray).push(data.formData);
      }
    });

    this.facilitiesFormService.registerForDistrictHeatDeviceUpdate.subscribe(data => {
      this.baseDataFG.controls.heatingDevice = data.formData;
      this.baseDataFG.updateValueAndValidity();
      this.facilitiesFormService.registerForComponentFormUpdate.next({
        component: 'baseData',
        formData: this.baseDataFG,
        index: this.currentIndex,
      });
    });
  }

  onFacilityTypeChange(event: any = null): void {
    if (event) {
      this.selectedFacilityType = event.value;
    }

    if (this.selectedFacilityType === 'other') {
      this.baseDataFG.addControl('otherType', new FormControl(!event ? this.baseData?.otherType || '' : '', [Validators.required]));
      this.baseDataFG.controls.otherType.markAsTouched();
    } else {
      this.baseDataFG.removeControl('otherType');
    }
    if (this.currentFormType.key === 'power' && this.showRegistrationInputField) {
      if (event?.value === 'commercial') {
        this.baseDataFG.addControl('registration', new FormControl(this.baseData?.registration || '', [Validators.required]));
      } else {
        this.baseDataFG.get('registration')?.reset();
        this.baseDataFG.removeControl('registration');
      }
    }
    if (this.showGasUsageForComercial) {
      if (event?.value === 'commercial') {
        this.baseDataFG.addControl('gasUsageFor', new FormControl(this.baseData?.gasUsageFor || ''));
      } else {
        this.baseDataFG.get('gasUsageFor')?.reset();
      }
    }

    this.updatePlaceOfMeterControlValidation();
  }

  addGasDevice(): void {
    this.gasDeviceCount.push(this.gasDeviceCount.length ? this.gasDeviceCount[this.gasDeviceCount.length - 1] + 1 : 0);
  }

  addSpecialAppliance(): void {
    this.specialApplianceCount.push(
      this.specialApplianceCount.length ? this.specialApplianceCount[this.specialApplianceCount.length - 1] + 1 : 0
    );
  }

  addWaterDevice(): void {
    this.waterDeviceCount.push(this.waterDeviceCount.length ? this.waterDeviceCount[this.waterDeviceCount.length - 1] + 1 : 0);
  }

  updateParentOnInitialize(): void {
    this.facilitiesFormService.manageBaseDataFormData({
      component: 'baseData',
      formData: this.baseDataFG,
      index: this.currentIndex,
    });
  }

  removeElement(): void {
    this.deleteComponent.emit(this.currentIndex);
  }

  removeChildElements(index: number): void {
    const elementName = this.currentFormType.childElement;
    const deviceId = (this.baseDataFG.controls[elementName] as FormArray).value[index]?.deviceId;

    let requestUrl: string;
    switch (this.currentFormType.index) {
      case 0:
        requestUrl = UrlSegments.UpdatePowerSpecialConsumer;
        break;
      case 1:
        requestUrl = UrlSegments.UpdateGasDevice;
        break;
      case 2:
        requestUrl = UrlSegments.UpdateWaterDevice;
        break;
    }
    if (deviceId && requestUrl) {
      this.communicationService.deleteData(`${requestUrl}=${deviceId}`).subscribe(
        response => {
          this.errorService.throwErrorIfFailure(response, true);
          this.handleDeviceData(elementName, index);
        },
        error => {
          this.errorService.throwErrorIfFailure(error, true);
        }
      );
    } else {
      this.handleDeviceData(elementName, index);
    }
  }

  get isPeakFlowRateRequired(): boolean {
    return (
      this.currentFormType.key === 'water' &&
      (this.currentInstallationFlow === InstallationFlowType.UpdateInstallation ||
        this.currentInstallationFlow === InstallationFlowType.NewInstallation) &&
      this.environmentService.isPeakFlowRequired
    );
  }

  get isNewInstallation() {
    return this.currentInstallationFlow === InstallationFlowType.NewInstallation;
  }

  private modifyDropdownCollections(): void {
    if (this.hiddenDropdownData?.generationFacility) {
      this.facilityTypes = this.facilityTypes.filter(ft => ft.value !== 'generation_facility');
    }
  }

  private handleDeviceData(elementName: string, currentIndex: number) {
    this[`${elementName}Count`].splice(currentIndex, 1);
    (this.baseDataFG.controls[elementName] as FormArray).controls.splice(currentIndex, 1);
    (this.baseDataFG.controls[elementName] as FormArray).updateValueAndValidity();
    if (this[`${elementName}Data`].length) {
      this[`${elementName}Data`].splice(currentIndex, 1);
    }
  }

  selectPlaceOfMeter(event): void {
    if (event.value === 'other') {
      this.baseDataFG.controls.otherLocation.setValidators([Validators.required]);
      this.baseDataFG.controls.otherLocation.markAsTouched();
    } else {
      this.baseDataFG.controls.otherLocation.clearValidators();
    }
    this.baseDataFG.controls.otherLocation.updateValueAndValidity();
  }

  evaluateDeviceTextVisibility() {
    const specialAppliances = this.baseDataFG.value.specialAppliance || [];
    const filteredAppliance =
      specialAppliances && specialAppliances.filter(item => ['heatpump', 'off_peak', 'other'].indexOf(item.specialAppliance) > -1);

    this.showDeviceAreaText = filteredAppliance.length > 0;
    this.cdr.detectChanges();
  }

  isValid(): boolean {
    return this.baseDataFG.valid;
  }

  private resetData(): void {
    this.selectedFacilityType = '';
    this.specialApplianceCount = [];
    this.gasDeviceCount = [];
    this.waterDeviceCount = [];
    this.baseData = null;
    this.formSubscription.unsubscribe();
  }

  private updateTotalHeatOutputData(devices: { power?: number; requiredFlowPressure: any }[]) {
    const totalHeatOutput = devices.reduce((totalHeatOutput, gasDevice) => totalHeatOutput + Number(gasDevice.power || 0), 0);

    const maxRequiredFlowPressure = devices
      .map(device => Number(device.requiredFlowPressure || -1))
      .reduce(
        (maxRequiredFlowPressure, requiredFlowPressure) =>
          requiredFlowPressure > maxRequiredFlowPressure ? requiredFlowPressure : maxRequiredFlowPressure,
        0
      );

    this.baseDataFG.controls.totalHeatOutput.setValue(totalHeatOutput);

    if (totalHeatOutput >= 500 || maxRequiredFlowPressure > 23) {
      this.baseDataFG.addControl(
        'isPowerConnectionForRemoteMeterReadingAvailable',
        new FormControl(
          {
            value: this.baseData?.isPowerConnectionForRemoteMeterReadingAvailable || '',
            disabled: this.isReadOnly,
          },
          Validators.required
        )
      );
    } else {
      this.baseDataFG.removeControl('isPowerConnectionForRemoteMeterReadingAvailable');
    }
  }

  private updatePlaceOfMeterControlValidation() {
    if (!this.showFacilityAdditionalData) {
      this.baseDataFG.controls.placeOfMeter.clearValidators();
    } else if (!this.isHeatingForm) {
      if (this.isPlaceOfMeterOptional) {
        this.baseDataFG.controls.placeOfMeter.clearValidators();
        this.baseDataFG.controls.placeOfMeter.setValue(null);
      } else {
        this.baseDataFG.controls.placeOfMeter.setValidators([Validators.required]);
      }
    }

    this.baseDataFG.controls.placeOfMeter.updateValueAndValidity();
  }
}
